import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import libphonenumber from 'google-libphonenumber';
import { Form, Input, Button, Row, Col, Select } from 'antd';
import { FormattedMessage } from "react-intl";
import countryList from 'react-select-country-list';
import { cc, cf, cw, hp } from '../../utils';
import { isValidPhoneNumber } from "libphonenumber-js";

import AuthHeader from './Header';
import { getCookie } from '../../utils/helpers';

const SignUp = (props) => {
    const { signUp, intl, _utils, _auth: { signingIn } } = props;

    const [form] = Form.useForm();
    const recaptchaRef = useRef(null);

    const [country, setCountry] = useState({
        location: {},
        country_name: 'Ghana',
        country_code: 'gh'
    });
    const [countries] = useState(countryList().getData());
    const [, setRecaptcha] = useState(null);
    const [screen, setScreen] = useState(0);

    useEffect(() => {
        ipinfo();
    }, []);

    const ipinfo = () => {
        axios({
            method: 'POST',
            url: `${hp.getApiBaseUrl()}/ipinfo`,
            json: true
        }).then((res) => {
            if (res.data.country_name) {
                setCountry(res.data);
            }
        }).catch(() => {});
    };

    const onChangeRecap = value => {
        setRecaptcha(value);
    };

    const asyncAvailable = async (name, value) => {
        return await axios({
            method: 'GET',
            url: `${hp.getApiBaseUrl()}/auth/validations/${name}s/${value}`,
            json: false
        }).then((res) => {
            if (res.data.statusCode === 200) {
                return res.data.data.userExists;
            } else {
                return false;
            }
        }).catch(() => {
            return false;
        });
    };

    const chheckEmailAddress = async (_, value) => {
        if (!value) {
            return Promise.resolve();
        }
        const checker = await asyncAvailable('email', value);
        if (checker === true) {
            return Promise.reject(`${value} already exists`);
        }
        return Promise.resolve();
    };

    const checkNumber = phone => {
        try {
            return libphonenumber.PhoneNumberUtil.getInstance().isPossibleNumber(
                libphonenumber.PhoneNumberUtil.getInstance().parse(phone, '')
            );
        } catch (error) {
            return false;
        }
    };

    const isPhoneNumber = (phone, country_code) => {
        try {
            return isValidPhoneNumber(phone, country_code);
        } catch (error) {
            return false;
        }
    };

    const checkPhoneNumber = async (_, value) => {
        const prefix = form.getFieldValue('prefix');

        if (!value) {
            return Promise.resolve();
        }

        const num = isPhoneNumber(`${prefix}${value}`, country.country_code);
        const state = checkNumber(`${prefix}${value}`);

        const newPhone = `${prefix}${value.replace(/^0/, '')}`;
        const checker = await asyncAvailable('msisdn', newPhone.trim());
        if (checker === true) {
            return Promise.reject(`${value} already exists`);
        }
        if (!num || !state) {
            return Promise.reject(intl.formatMessage(cw.formErrorMsgNotValidPhoneNumber));
        }
        return Promise.resolve();
    };

    const confirmPassword = (_, value) => {
        if (!value) {
            return Promise.resolve();
        }
        if (value !== form.getFieldValue('password')) {
            return Promise.reject(intl.formatMessage(cw.formErrorMsgMismatchPassword));
        }
        return Promise.resolve();
    };

    const getStarted = () => {
        form.validateFields(['organizationName', 'industry'])
            .then(() => {
                setScreen(1);
            })
            .catch(() => {});
    };

    const _signup = (v) => {
        const newPhone = `${v.prefix}${v.msisdn.replace(/^0/, '')}`;
        signUp(intl, _utils.lang, {
            firstName: v.firstName,
            lastName: v.lastName,
            email: v.email.trim(),
            msisdn: newPhone.trim(),
            userCountry: v.country,
            organizationName: v.organizationName,
            industry: v.industry,
            organizationCountry: v.organizationCountry,
            password: v.password,
            preferences: { language: _utils.lang, promoCode: getCookie('promoCode', true) }
        });
    };

    return (
        <React.Fragment>
            <Form
                id="esoko__signin"
                form={form}
                layout="vertical"
                requiredMark={false}
                onFinish={_signup}
                initialValues={{
                    organizationCountry: 'Ghana',
                    country: 'Ghana',
                    prefix: `+${country.location && country.location.calling_code ? country.location.calling_code : '233'}`
                }}
            >
                <AuthHeader screen="signup" />

                {/* Organisation details */}
                <Row gutter={16} className={screen === 1 ? 'hide' : ''}>
                    <Col span={24}>
                        <p><b><FormattedMessage id="Signup.Title.Organisation" defaultMessage="Please provide the details of your organization" /></b></p>
                    </Col>
                    <Col span={24}>
                        <div>
                            <Form.Item
                                colon={false}
                                name="organizationName"
                                label={<FormattedMessage id="Label.Organisation" defaultMessage="Organisation name" />}
                                rules={[
                                    { required: true, message: <FormattedMessage id="Rules.Organisation.Required" defaultMessage="Please provide organisation" /> },
                                    { min: 2, message: <FormattedMessage id="Rules.Organisation.Min2" defaultMessage="Organisation name must be at least 2 characters" /> },
                                    { max: 50, message: <FormattedMessage id="Rules.Organisation.Max50" defaultMessage="Organisation name cannot be longer than 50 characters" /> },
                                ]}
                            >
                                <Input size="large" type="text" autoComplete="off" sso-id="signup-organizationName" />
                            </Form.Item>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div>
                            <Form.Item
                                colon={false}
                                name="industry"
                                label={<FormattedMessage id="Label.Industry" defaultMessage="Industry" />}
                                rules={[
                                    { required: true, message: <FormattedMessage id="Rules.Industry.Required" defaultMessage="Please provide industry" /> }
                                ]}
                            >
                                <Select
                                    size="large"
                                    showSearch={Object.keys(_utils.industries).length > 5}
                                    sso-id="signup-industry"
                                    placeholder={intl.formatMessage(cw['Placeholder.Industry'])}
                                >
                                    {Object.keys(_utils.industries).map(industry => (
                                        <Select.Option key={industry} value={industry}>{_utils.industries[industry]}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            colon={false}
                            name="organizationCountry"
                            label={<FormattedMessage id="Label.Country" defaultMessage="Country" />}
                            rules={[
                                { required: true, message: <span /> },
                            ]}
                        >
                            <Select size="large" showSearch={true} sso-id="signup-organizationCountry">
                                {countries.map(cnt => (
                                    <Select.Option key={cnt.label} value={cnt.label}>{cnt.label}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.organizationName !== cur.organizationName || prev.industry !== cur.industry}>
                            {({ getFieldsValue }) => {
                                const values = getFieldsValue(['organizationName', 'industry']);
                                const disabled = !values.organizationName || !values.industry;
                                return (
                                    <Form.Item>
                                        <Button
                                            className="submitBtn"
                                            block
                                            size="large"
                                            type="primary"
                                            htmlType="button"
                                            onClick={getStarted}
                                            sso-id="signup-proceed"
                                            disabled={disabled}
                                        >
                                            <FormattedMessage id="Button.Proceed" defaultMessage="Proceed" />
                                        </Button>
                                    </Form.Item>
                                );
                            }}
                        </Form.Item>
                    </Col>
                </Row>

                {/* Personal details */}
                <Row gutter={16} className={screen === 0 ? 'hide' : ''}>
                    <Col span={24}>
                        <p><b><FormattedMessage id="Signup.Title.Personal" defaultMessage="Enter your personal details" /></b></p>
                    </Col>
                    <Col span={12}>
                        <div>
                            <Form.Item
                                colon={false}
                                name="firstName"
                                label={<FormattedMessage id="Label.FirstName" defaultMessage="First Name" />}
                                rules={[
                                    { required: true, message: <FormattedMessage id="Rules.FirstName.Required" defaultMessage="Please provide first name" /> }
                                ]}
                            >
                                <Input size="large" type="text" autoComplete="off" sso-id="signup-firstname" />
                            </Form.Item>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div>
                            <Form.Item
                                colon={false}
                                name="lastName"
                                label={<FormattedMessage id="Label.LastName" defaultMessage="Last Name" />}
                                rules={[
                                    { required: true, message: <FormattedMessage id="Rules.LastName.Required" defaultMessage="Please provide last name" /> }
                                ]}
                            >
                                <Input size="large" type="text" autoComplete="off" sso-id="signup-lastname" />
                            </Form.Item>
                        </div>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            colon={false}
                            name="email"
                            label={<FormattedMessage id="Label.EmailAddress" defaultMessage="Email Address" />}
                            rules={[
                                { required: true, message: <FormattedMessage id="Rules.EmailAddress.Required" defaultMessage="Please provide email address" /> },
                                { type: 'email', message: <FormattedMessage id="Rules.EmailAddress.Invalid" defaultMessage="Please check provided email" /> },
                                { validator: chheckEmailAddress, validateTrigger: 'onBlur' }
                            ]}
                            validateTrigger={['onChange', 'onBlur']}
                        >
                            <Input size="large" type="email" autoComplete="off" sso-id="signup-email" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            colon={false}
                            name="msisdn"
                            label={<FormattedMessage id="Label.PhoneNumber" defaultMessage="Phone Number" />}
                            rules={[
                                { required: true, message: <FormattedMessage id="Rules.PhoneNumber.Required" defaultMessage="Please provide phone number" /> },
                                { validator: checkPhoneNumber, validateTrigger: 'onBlur' }
                            ]}
                            validateTrigger={['onChange', 'onBlur']}
                        >
                            <Input
                                size="large"
                                type="number"
                                addonBefore={
                                    <Form.Item
                                        name="prefix"
                                        noStyle
                                        initialValue={`+${country.location && country.location.calling_code ? country.location.calling_code : '233'}`}
                                    >
                                        <Select
                                            size="large"
                                            placeholder="+233"
                                            showSearch
                                            style={{ width: '6vw' }}
                                            onChange={e => {
                                                const selectedCountry = cc.find(c => c.dialCode === e);
                                                setCountry({
                                                    ...country,
                                                    location: { calling_code: selectedCountry.dialCode.replace('+', '') },
                                                    country_name: selectedCountry.name,
                                                    country_code: selectedCountry.countryCode,
                                                });
                                            }}
                                        >
                                            {cc.map(c => (
                                                <Select.Option key={c.countryCode} value={c.dialCode}>
                                                    {c.dialCode}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                }
                                autoComplete="off"
                                sso-id="signup-msisdn"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            colon={false}
                            name="country"
                            label={<FormattedMessage id="Label.Country" defaultMessage="Country" />}
                            rules={[
                                { required: true, message: <span /> },
                            ]}
                        >
                            <Select size="large" showSearch={true} sso-id="signup-userCountry">
                                {countries.map(cnt => (
                                    <Select.Option key={cnt.label} value={cnt.label}>{cnt.label}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            colon={false}
                            name="password"
                            label={<FormattedMessage id="Label.Password" defaultMessage="Password" />}
                            rules={[
                                { required: true, message: <FormattedMessage id="Rules.Password.Required" defaultMessage="Please provide password" /> },
                                { min: 6, message: <FormattedMessage id="Rules.Password.Min6" defaultMessage="Password must be at least 6 characters" /> }
                            ]}
                        >
                            <Input.Password size="large" type="password" minLength="6" autoComplete="off" sso-id="signup-password" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            colon={false}
                            name="cpassword"
                            label={<FormattedMessage id="Label.ConfirmPassword" defaultMessage="Confirm Password" />}
                            rules={[
                                { required: true, message: <FormattedMessage id="Rules.Password.Required" defaultMessage="Please provide password" /> },
                                { min: 6, message: <FormattedMessage id="Rules.Password.Min6" defaultMessage="Password must be at least 6 characters" /> },
                                { validator: confirmPassword }
                            ]}
                        >
                            <Input.Password size="large" type="password" minLength="6" autoComplete="off" sso-id="signup-confirmPassword" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <div className="recaptchaDiv">
                            <ReCAPTCHA data-size="compact" ref={recaptchaRef} size="invisible" sitekey={cf.REACT_RECAPTCHA_SITE_KEY} onChange={onChangeRecap} />
                        </div>
                    </Col>
                    <Col span={24}>
                        <Form.Item>
                            <Button className="submitBtn" block size="large" type="primary" htmlType="submit" loading={signingIn} sso-id="signup-button">
                                <FormattedMessage id="Button.SIgnup" defaultMessage="Sign up" />
                            </Button>
                        </Form.Item>
                    </Col>
                    <Col span={24} style={{ textAlign: 'center' }}>
                        <p><a href={`/${_utils.lang || 'en'}/signup${window.location.search}#changedetails`} onClick={() => setScreen(0)}>Change Organization Details</a></p>
                    </Col>
                </Row>

                <div className="formLinks">
                    <p style={{ textAlign: 'center' }}>
                        <span className="noAccountText"><FormattedMessage id="auth_signin_got_account" defaultMessage="Already have an account?" /></span>
                        <Link to={`/${_utils.lang || 'en'}/signin${window.location.search}`} sso-id="login-link"><FormattedMessage id="auth_signin_signin" defaultMessage="Sign in" /></Link>
                    </p>
                </div>
            </Form>
        </React.Fragment>
    );
};

export default SignUp;